import { scrollToBottom } from './chat';
import { sessionState } from '../state';
import type { RuntimeActivitySegment } from './chat/types';

function agentLabel(agent: unknown): string {
  const normalized = String(agent || '').trim().toLowerCase();
  if (normalized === 'opencode') return 'OpenCode';
  if (normalized === 'genericagent') return 'GenericAgent';
  if (normalized === 'hermes') return 'Hermes';
  if (normalized === 'codex') return 'Codex';
  if (normalized === 'claude') return 'Claude';
  return String(agent || 'Runtime');
}

function kindLabel(kind: unknown): string {
  const normalized = String(kind || '').trim().toLowerCase();
  if (normalized === 'subagent') return '子任务';
  if (normalized === 'background_task') return '后台任务';
  if (normalized === 'reasoning') return '推理';
  if (normalized === 'command') return '命令';
  if (normalized === 'file_change') return '文件变更';
  if (normalized === 'mcp') return 'MCP';
  if (normalized === 'tool') return '工具';
  return '活动';
}

function statusLabel(status: unknown): string {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'queued') return '排队中';
  if (normalized === 'completed') return '已完成';
  if (normalized === 'failed') return '失败';
  return '进行中';
}

function statusClass(status: unknown): string {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'queued') return 'queued';
  if (normalized === 'completed') return 'done';
  if (normalized === 'failed') return 'error';
  return 'running';
}

function formatClock(value: unknown): string {
  const date = value ? new Date(String(value)) : null;
  if (!date || Number.isNaN(date.getTime())) return '--:--:--';
  return date.toLocaleTimeString([], { hour12: false });
}

function trimText(value: unknown): string {
  if (typeof value === 'string') return value.replace(/\s+/g, ' ').trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (!value || typeof value !== 'object') return '';
  try {
    return JSON.stringify(value).replace(/\s+/g, ' ').trim();
  } catch {
    return '';
  }
}

type RuntimeActivityViewState = {
  details: HTMLDetailsElement;
  summaryLabel: HTMLSpanElement;
  summaryTitle: HTMLSpanElement;
  summaryMeta: HTMLSpanElement;
  statusBadge: HTMLSpanElement;
};

function createRuntimeActivityBody(msg: RuntimeActivitySegment | any): HTMLDivElement {
  const body = document.createElement('div');
  body.className = 'runtime-activity-body';

  const detail = trimText(msg?.detail);
  if (detail) {
    const detailEl = document.createElement('div');
    detailEl.className = 'runtime-activity-body-detail';
    detailEl.textContent = detail;
    body.appendChild(detailEl);
  }

  const metaBits = [
    formatClock(msg?.timestamp),
    trimText(msg?.sourceEventType),
    trimText(msg?.parentActivityId) ? `parent:${trimText(msg.parentActivityId)}` : '',
  ].filter(Boolean);
  if (metaBits.length > 0) {
    const meta = document.createElement('div');
    meta.className = 'runtime-activity-body-meta';
    meta.textContent = metaBits.join(' · ');
    body.appendChild(meta);
  }

  return body;
}

export function createRuntimeActivityElement(msg: RuntimeActivitySegment | any): HTMLDivElement {
  const root = document.createElement('div');
  root.className = 'runtime-activity-msg msg-segment';
  root.dataset.activityId = String(msg?.activityId || '');

  const details = document.createElement('details');
  details.className = 'runtime-activity-card';
  details.open = String(msg?.status || '').toLowerCase() !== 'completed';

  const summary = document.createElement('summary');
  summary.className = 'runtime-activity-summary';

  const summaryMain = document.createElement('span');
  summaryMain.className = 'runtime-activity-summary-main';

  const label = document.createElement('span');
  label.className = 'runtime-activity-summary-label';
  summaryMain.appendChild(label);

  const title = document.createElement('span');
  title.className = 'runtime-activity-summary-title';
  summaryMain.appendChild(title);

  const summaryMeta = document.createElement('span');
  summaryMeta.className = 'runtime-activity-summary-meta';
  summaryMain.appendChild(summaryMeta);

  const badge = document.createElement('span');
  badge.className = 'runtime-activity-badge';

  summary.appendChild(summaryMain);
  summary.appendChild(badge);

  const body = createRuntimeActivityBody(msg);

  details.appendChild(summary);
  details.appendChild(body);
  root.appendChild(details);
  renderActivitySummary({
    details,
    summaryLabel: label,
    summaryTitle: title,
    summaryMeta,
    statusBadge: badge,
  }, msg);
  return root;
}

function renderActivitySummary(view: RuntimeActivityViewState, msg: any): void {
  const agent = agentLabel(msg?.agent);
  const kind = kindLabel(msg?.kind);
  const title = trimText(msg?.title) || `${agent} ${kind}`;
  const detail = trimText(msg?.detail);
  const status = statusLabel(msg?.status);
  const statusTone = statusClass(msg?.status);

  view.summaryLabel.textContent = `${agent} ${kind}`;
  view.summaryTitle.textContent = title;
  view.summaryMeta.textContent = detail || `活动 ID: ${String(msg?.activityId || '').trim()}`;
  view.statusBadge.className = `runtime-activity-badge ${statusTone}`;
  view.statusBadge.textContent = status;

  if (statusTone === 'running' || statusTone === 'queued') {
    view.details.open = true;
  }
}

export function handleRuntimeActivityMessage(msg: any): void {
  const sessionId = String(msg?.sessionId || sessionState.currentSessionId || '').trim();
  if (!sessionId || !msg?.activityId) return;
  scrollToBottom();
}
